import type { TrackError } from '@opendoor/auth-fe/context';
import {
  SESSION_RATE_LIMITED_ERROR_NAME,
  SessionRateLimitedError,
} from '@opendoor/auth-fe/context';

import type { useObservability } from '../../helpers/observability';
import { isRateLimitedZeusReadError, isRetryableZeusReadError } from '../api/zeusBrowserClient';

/**
 * `error.name` for a ViewerSession read that never got a response: the browser
 * cancelled it (navigation, tab close) or the network dropped it. auth-fe's
 * session failure report forwards only `error.name`, so this name is the
 * signal consumers use to route client transport noise away from Sentry. It
 * must stay a literal: bundlers mangle class names, so `<class>.name` cannot
 * be trusted.
 */
export const VIEWER_SESSION_TRANSPORT_ERROR_NAME = 'ViewerSessionTransportError';

// Transport errors can embed session credentials; report one scrubbed message
// for every session-read failure.
const VIEWER_SESSION_FAILURE_MESSAGE = 'ViewerSession request failed';

class ViewerSessionTransportError extends Error {
  constructor() {
    super(VIEWER_SESSION_FAILURE_MESSAGE);
    this.name = VIEWER_SESSION_TRANSPORT_ERROR_NAME;
  }
}

/**
 * Scrubbed failure for a rejected ViewerSession read. Fetch-level rejections
 * keep their transport class so `createTrackAuthError` can route them around
 * Sentry; rate-limited reads keep the class that carries `rateLimited` through
 * the auth state; everything else collapses to a generic error.
 */
export const sessionReadFailure = (error: unknown): Error => {
  if (isRetryableZeusReadError(error)) {
    return new ViewerSessionTransportError();
  }
  if (isRateLimitedZeusReadError(error)) {
    return new SessionRateLimitedError();
  }
  return new Error(VIEWER_SESSION_FAILURE_MESSAGE);
};

type ScopedTrackError = ReturnType<typeof useObservability>['trackError'];

/**
 * auth-fe reports every failed session read through `trackError`, and on
 * transient pages the browser usually cancels that read: /token hard-navigates
 * right after a magic-link redeem, so the failure is the unload, not Zeus.
 * Transport-class failures stay in Datadog RUM; response-level failures (HTTP
 * errors, GraphQL errors, session-shape mismatches) still reach Sentry. Sentry
 * groups by the label fingerprint, so the rate-limited class reports under its
 * own label to leave the generic session-error group.
 */
export const createTrackAuthError =
  (trackError: ScopedTrackError): TrackError =>
  (label, error, data) =>
    trackError(
      data?.errorName === SESSION_RATE_LIMITED_ERROR_NAME ? 'Session error (rate limited)' : label,
      error,
      data,
      { skipSentry: data?.errorName === VIEWER_SESSION_TRANSPORT_ERROR_NAME },
    );
